import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControlLabel,
  Typography,
  Chip,
  Box,
} from "@material-ui/core";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  sectionLabel: {
    fontSize: "0.78rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  statusRow: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  multiSelect: {
    "& .MuiSelect-select": {
      minHeight: 36,
    },
  },
  chipBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 4,
  },
}));

const STATUS_OPTIONS = [
  { value: "open", label: "Aberto", color: "#2196F3" },
  { value: "pending", label: "Pendente", color: "#FF9800" },
  { value: "closed", label: "Fechado", color: "#4CAF50" },
];

const ColumnFilterBuilder = ({ columnType, filterJson, onChange, companyId }) => {
  const classes = useStyles();
  const [queues, setQueues] = useState([]);
  const [tags, setTags] = useState([]);
  const [users, setUsers] = useState([]);

  // Fetch options
  useEffect(() => {
    if (columnType === "queue" || columnType === "custom") {
      api.get("/queue").then(({ data }) => setQueues(data || [])).catch(() => {});
    }
    if (columnType === "tag" || columnType === "custom") {
      api.get("/tags/list").then(({ data }) => setTags(data || [])).catch(() => {});
    }
    if (columnType === "assignee" || columnType === "custom") {
      api.get("/users/list").then(({ data }) => setUsers(data || [])).catch(() => {});
    }
  }, [columnType]);

  const f = filterJson || {};

  const updateFilter = (key, value) => {
    onChange({ ...f, [key]: value });
  };

  return (
    <div className={classes.root}>
      {/* Status (always shown) */}
      <div className={classes.section}>
        <Typography className={classes.sectionLabel}>Status</Typography>
        <div className={classes.statusRow}>
          {STATUS_OPTIONS.map((s) => (
            <FormControlLabel
              key={s.value}
              control={
                <Checkbox
                  checked={(f.status || []).includes(s.value)}
                  onChange={(e) => {
                    const current = f.status || [];
                    const next = e.target.checked
                      ? [...current, s.value]
                      : current.filter((v) => v !== s.value);
                    updateFilter("status", next);
                  }}
                  size="small"
                  style={{ color: s.color }}
                />
              }
              label={
                <Typography variant="body2" style={{ fontSize: "0.82rem" }}>
                  {s.label}
                </Typography>
              }
            />
          ))}
        </div>
      </div>

      {/* Queue */}
      {(columnType === "queue" || columnType === "custom") && queues.length > 0 && (
        <div className={classes.section}>
          <Typography className={classes.sectionLabel}>Filas</Typography>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Filas</InputLabel>
            <Select
              multiple
              value={f.queueIds || []}
              onChange={(e) => updateFilter("queueIds", e.target.value)}
              label="Filas"
              renderValue={(selected) => (
                <div className={classes.chipBox}>
                  {selected.map((id) => {
                    const q = queues.find((q) => q.id === id);
                    return (
                      <Chip
                        key={id}
                        label={q?.name || id}
                        size="small"
                        style={{
                          backgroundColor: q?.color || "#757575",
                          color: "#fff",
                          height: 20,
                          fontSize: "0.7rem",
                        }}
                      />
                    );
                  })}
                </div>
              )}
            >
              {queues.map((q) => (
                <MenuItem key={q.id} value={q.id}>
                  <Checkbox
                    checked={(f.queueIds || []).includes(q.id)}
                    size="small"
                  />
                  <ListItemText
                    primary={q.name}
                    style={{ marginLeft: 4 }}
                  />
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: q.color,
                      marginLeft: 8,
                    }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={f.noQueue || false}
                onChange={(e) => updateFilter("noQueue", e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2" style={{ fontSize: "0.82rem" }}>
                Incluir tickets sem fila
              </Typography>
            }
          />
        </div>
      )}

      {/* Tags */}
      {(columnType === "tag" || columnType === "custom") && tags.length > 0 && (
        <div className={classes.section}>
          <Typography className={classes.sectionLabel}>Tags</Typography>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Tags</InputLabel>
            <Select
              multiple
              value={f.tagIds || []}
              onChange={(e) => updateFilter("tagIds", e.target.value)}
              label="Tags"
              renderValue={(selected) => (
                <div className={classes.chipBox}>
                  {selected.map((id) => {
                    const t = tags.find((t) => t.id === id);
                    return (
                      <Chip
                        key={id}
                        label={t?.name || id}
                        size="small"
                        style={{
                          backgroundColor: t?.color || "#E0E0E0",
                          color: "#fff",
                          height: 20,
                          fontSize: "0.7rem",
                        }}
                      />
                    );
                  })}
                </div>
              )}
            >
              {tags.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  <Checkbox
                    checked={(f.tagIds || []).includes(t.id)}
                    size="small"
                  />
                  <ListItemText primary={t.name} style={{ marginLeft: 4 }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )}

      {/* Assignee */}
      {(columnType === "assignee" || columnType === "custom") && users.length > 0 && (
        <div className={classes.section}>
          <Typography className={classes.sectionLabel}>Atendentes</Typography>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Atendentes</InputLabel>
            <Select
              multiple
              value={f.userIds || []}
              onChange={(e) => updateFilter("userIds", e.target.value)}
              label="Atendentes"
              renderValue={(selected) => (
                <div className={classes.chipBox}>
                  {selected.map((id) => {
                    const u = users.find((u) => u.id === id);
                    return (
                      <Chip
                        key={id}
                        label={u?.name || id}
                        size="small"
                        variant="outlined"
                        style={{ height: 20, fontSize: "0.7rem" }}
                      />
                    );
                  })}
                </div>
              )}
            >
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  <Checkbox
                    checked={(f.userIds || []).includes(u.id)}
                    size="small"
                  />
                  <ListItemText primary={u.name} style={{ marginLeft: 4 }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={f.noUser || false}
                onChange={(e) => updateFilter("noUser", e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2" style={{ fontSize: "0.82rem" }}>
                Incluir tickets sem atendente
              </Typography>
            }
          />
        </div>
      )}

      {/* Unread only */}
      <FormControlLabel
        control={
          <Checkbox
            checked={f.unreadOnly || false}
            onChange={(e) => updateFilter("unreadOnly", e.target.checked)}
            size="small"
          />
        }
        label={
          <Typography variant="body2" style={{ fontSize: "0.82rem" }}>
            Somente com mensagens não lidas
          </Typography>
        }
      />
    </div>
  );
};

export default ColumnFilterBuilder;
